/**
 * FLY SINGLE MISSION, as a state machine.
 *
 * The original's flow, from the captures and `ui-menus.md`: the grid picks a
 * mission type, the side picker asks who you fly for, a briefing screen
 * describes the sortie with `RETURN TO MAIN` / `REALISM PANEL` / `ACCEPT`,
 * and `ACCEPT` leads to `MISSION ASSIGNMENT` → `FLIGHT ASSIGNMENTS` →
 * `BEGIN MISSION`.
 *
 * **A Historic Encounter is the one exception, and it is the original's**
 * (G19, user's call 2026-08-26): its briefing's third button is `BEGIN` and it
 * navigates straight to `/fly`. The two screens it skips are where a sortie's
 * *changeable* parameters live, and an encounter's are fixed by
 * `HISTORIC.DAT` — `missions-screens.ts`'s `briefingLaunchAction` carries the
 * argument and the string-dump evidence. Nothing in this module needed
 * changing for it: `begin` already launches from any view, because
 * `missionsLaunch` reads the model rather than the screen.
 *
 * This module is that flow and nothing else: no pixels, no
 * canvas, no router. `missions-screens.ts` turns a model into widgets and the
 * route turns a `navigate` effect into a real navigation.
 *
 * **Where the re-roll went.** The modern screen had a `ROLL A NEW FRONT`
 * button that re-seeded every card at once. The original has no such control,
 * and does not need one: picking a mission type *is* the roll, because
 * `PS.EXE` draws a fresh sortie every time you enter one. So the seed advances
 * on every `slot:` command through the same `nextSeed` LCG the button used —
 * back out of a briefing you do not like, pick the type again, and the
 * weather, the aeroplanes and the opposition are all different. The sequence
 * from a given start seed is still reproducible, and the seed still travels in
 * the `/fly` URL, so a link flies the identical sortie.
 *
 * **Editable Mission Parameters (AD-011).** The manual's Mission Setup screen
 * — "changeable options are displayed as a button in the text. Clicking on
 * one will bring up a menu with your possible options" — is this port's
 * boxed briefing values and the Flight Assignments value fields, now wired
 * rather than decorative. Four fields are overridable: `playerAircraft`,
 * `enemyAircraft` (which also covers a Dogfight a Famous Ace opponent's
 * mount — the manual's own "put the Red Baron in a Camel" example), `region`
 * and `ammunition`. All four start `undefined`, meaning "let the generator
 * draw it" — an untouched screen flies exactly the mission it always did,
 * and `chooseSlot` clears them on every fresh type pick, the same reset the
 * seed itself gets, so last mission's choices never leak into this one's.
 *
 * **Weather Selection (roadmap 11s).** Three more fields join the four
 * above, on the identical pattern: `windStrength`, `sky`, `timeOfDay`. The
 * original boxes all three in the same Mission Setup sentence — measured
 * live, `re-sessions/calib/shots/nav-setup.png` and `nav-windpopup.png` show
 * `[daytime]`, `[light]` and `[partly cloudy]` boxed exactly like
 * `[Sopwith Pups]`, and the winds box opens a `CHOOSE` popup of exactly
 * `still`/`light`/`strong`/`CANCEL` (`flight-tunables.md` §8.5.5) — so this
 * port offers no more and no less. `windStrength` is the one with teeth:
 * since AD-007k §10 the wind word is stamped on `spec.weather.wind` and
 * scales `WEATHER_WIND_SCALE`, so picking `strong` changes the flown air.
 * `sky` and `timeOfDay` stay `WEATHER_IS_COSMETIC` — briefing text only —
 * exactly as before; they are pickable because the original lets you pick
 * them, not because a choice there changes anything yet.
 */

import {
  formationsFor,
  historicSeatPlayable,
  type AircraftKey,
  type AmmunitionType,
  type Formation,
  type MissionRegion,
  type Nation,
  type SkyCondition,
  type TimeOfDay,
  type WindStrength,
} from "@red-opus/engine";
import { missionLaunchPath, nextSeed, DEFAULT_SEED, type MissionLaunch } from "../../mission/mission-launch.ts";
import type { RealismPanel } from "../../mission/realism-settings.ts";
import { aerodromeStartDialog, sidePickerDialog } from "./mission-dialogs.ts";
export { FLIGHT_SIZE_WORDS, paramFieldOf, parameterDialog, type MissionParamField } from "./mission-dialogs.ts";
import {
  historicCommandTarget,
  historicPickerDialog,
  historicRefusalDialog,
  historicSideDialog,
} from "./historic-picker.ts";
import { gridSlotById, gridSlotReason, gridSlotTitle, type GridSlot } from "./mission-grid.ts";
import type { ShellEffect } from "./menu-model.ts";
import { noticeDialog, type ShellDialog } from "./shell-screen.ts";

/**
 * The screens this surface owns, in flow order. `realism` is reachable from
 * the briefing and from Flight Assignments, exactly as the captures show.
 * `bestMissions` is the score table `BEST MISSIONS` opens — a leaf with no
 * side picker or generated `MissionSpec` behind it, unlike every mission slot.
 */
export type MissionsView = "grid" | "briefing" | "assignment" | "roster" | "realism" | "bestMissions";

export interface MissionsModel {
  readonly view: MissionsView;
  /** The chosen grid slot, or nothing on the grid itself. */
  readonly slot: string | undefined;
  /**
   * The Historic Encounter catalog id the `SELECT HISTORIC MISSION:` popup
   * chose, when the slot is the historic picker. Every other slot leaves it
   * unset, and picking any slot clears it.
   */
  readonly historic: string | undefined;
  readonly nation: Nation;
  readonly seed: number;
  readonly realism: RealismPanel;
  readonly dialog: ShellDialog | undefined;
  readonly focus: number;
  /** Which view `REALISM PANEL`'s `ACCEPT` returns to. */
  readonly realismReturn: MissionsView;
  /** `undefined` means "let the generator draw it" — see the module note. */
  readonly playerAircraft: AircraftKey | undefined;
  readonly enemyAircraft: AircraftKey | undefined;
  readonly region: MissionRegion | undefined;
  readonly ammunition: AmmunitionType | undefined;
  readonly windStrength: WindStrength | undefined;
  readonly sky: SkyCondition | undefined;
  readonly timeOfDay: TimeOfDay | undefined;
  /** `FORMATION` and `TOTAL AIRCRAFT IN FLIGHT` — same "undefined means let the generator draw it" rule as the seven above. */
  readonly formation: Formation | undefined;
  readonly flightSize: number | undefined;
}

export function initialMissions(realism: RealismPanel, seed = DEFAULT_SEED): MissionsModel {
  return {
    view: "grid",
    slot: undefined,
    historic: undefined,
    nation: "allied",
    seed,
    realism,
    dialog: undefined,
    focus: 0,
    realismReturn: "briefing",
    playerAircraft: undefined,
    enemyAircraft: undefined,
    region: undefined,
    ammunition: undefined,
    windStrength: undefined,
    sky: undefined,
    timeOfDay: undefined,
    formation: undefined,
    flightSize: undefined,
  };
}

export interface MissionsTransition {
  readonly model: MissionsModel;
  readonly effect: ShellEffect | undefined;
}

function stay(model: MissionsModel): MissionsTransition {
  return { model, effect: undefined };
}

function go(model: MissionsModel, view: MissionsView): MissionsTransition {
  return stay({ ...model, view, focus: 0, dialog: undefined });
}

function chooseSlot(model: MissionsModel, slot: GridSlot): MissionsTransition {
  if (slot.target.kind === "return") return { model, effect: { kind: "navigate", path: "/" } };
  // No side picker, no seed roll: the score table is a leaf, not a sortie.
  if (slot.target.kind === "bestMissions") return go({ ...model, slot: slot.id }, "bestMissions");
  // `FLY A HISTORIC MISSION` picks the encounter first; the side picker and the
  // briefing follow from `historic:<id>` below, on the identical path a
  // straight-to-briefing slot takes.
  if (slot.target.kind === "picker") {
    return stay({ ...model, slot: slot.id, historic: undefined, dialog: historicPickerDialog() });
  }
  if (slot.target.kind !== "mission") {
    return stay({ ...model, dialog: noticeDialog(gridSlotTitle(slot), gridSlotReason(slot)) });
  }
  // The seed advances here and only here: choosing a type is the original's own re-roll.
  // Any parameters overridden for the last sortie reset with it — a fresh draw, fresh board.
  return stay({
    ...model,
    slot: slot.id,
    historic: undefined,
    seed: nextSeed(model.seed),
    dialog: sidePickerDialog(),
    playerAircraft: undefined,
    enemyAircraft: undefined,
    region: undefined,
    ammunition: undefined,
    windStrength: undefined,
    sky: undefined,
    timeOfDay: undefined,
    formation: undefined,
    flightSize: undefined,
  });
}

function chooseSide(model: MissionsModel, nation: Nation): MissionsTransition {
  // The one seat `HISTORIC.DAT` refuses gets `PS.EXE`'s own refusal rather than
  // a briefing for a mission that does not exist. Reachable only from a link or
  // a keyboard, since `historicSideDialog` does not draw the row.
  if (model.historic && !historicSeatPlayable(model.historic, nation)) {
    return stay({ ...model, dialog: historicRefusalDialog(nation) });
  }
  return stay({ ...model, nation, view: "briefing", focus: 0, dialog: undefined });
}

/** The launch URL for the sortie the model currently describes. */
export function missionsLaunchPath(model: MissionsModel): string | undefined {
  const launch = missionsLaunch(model);
  return launch ? missionLaunchPath(launch) : undefined;
}

/** The launch the model describes, for the route to generate a spec from. */
export function missionsLaunch(model: MissionsModel): MissionLaunch | undefined {
  const slot = model.slot ? gridSlotById(model.slot) : undefined;
  if (!slot) return undefined;
  // A picker slot has no catalog id of its own — the encounter the player chose
  // supplies it, and the seed goes along unchanged because a Historic Encounter
  // does not draw from it.
  const catalogId = slot.target.kind === "picker" ? model.historic : slot.target.kind === "mission" ? slot.target.catalogId : undefined;
  if (!catalogId) return undefined;
  return {
    catalogId,
    seed: model.seed,
    nation: model.nation,
    playerAircraft: model.playerAircraft,
    enemyAircraft: model.enemyAircraft,
    region: model.region,
    ammunition: model.ammunition,
    windStrength: model.windStrength,
    sky: model.sky,
    timeOfDay: model.timeOfDay,
    formation: model.formation,
    flightSize: model.flightSize,
  };
}

const VIEW_COMMANDS: Readonly<Record<string, MissionsView>> = {
  briefing: "briefing",
  assignment: "assignment",
  roster: "roster",
};

/**
 * Applies one named command. Unknown commands are ignored rather than
 * throwing: the realism panel's own commands are handled by the caller before
 * this runs, and a screen that grows a control should not be able to crash the
 * surface.
 */
export function missionsCommand(model: MissionsModel, command: string): MissionsTransition {
  if (command.startsWith("slot:")) {
    const slot = gridSlotById(command.slice(5));
    return slot ? chooseSlot(model, slot) : stay(model);
  }
  const encounter = historicCommandTarget(command);
  if (encounter) {
    // Picking an encounter is the historic slot's own re-roll step: it advances
    // the seed the way `chooseSlot` does for every other slot, then asks for a
    // side — and offers only the sides `HISTORIC.DAT` actually has.
    return stay({ ...model, historic: encounter, seed: nextSeed(model.seed), dialog: historicSideDialog(encounter) });
  }
  if (command === "side:german" || command === "side:allied") {
    return chooseSide(model, command === "side:german" ? "german" : "allied");
  }
  if (command.startsWith("set:aircraft:")) {
    return stay({ ...model, playerAircraft: command.slice(13) as AircraftKey, dialog: undefined });
  }
  if (command.startsWith("set:enemy:")) {
    return stay({ ...model, enemyAircraft: command.slice(10) as AircraftKey, dialog: undefined });
  }
  if (command.startsWith("set:region:")) {
    return stay({ ...model, region: command.slice(11) as MissionRegion, dialog: undefined });
  }
  if (command.startsWith("set:ammo:")) {
    return stay({ ...model, ammunition: command.slice(9) as AmmunitionType, dialog: undefined });
  }
  if (command.startsWith("set:wind:")) {
    return stay({ ...model, windStrength: command.slice("set:wind:".length) as WindStrength, dialog: undefined });
  }
  if (command.startsWith("set:sky:")) {
    return stay({ ...model, sky: command.slice("set:sky:".length) as SkyCondition, dialog: undefined });
  }
  if (command.startsWith("set:time:")) {
    return stay({ ...model, timeOfDay: command.slice("set:time:".length) as TimeOfDay, dialog: undefined });
  }
  if (command.startsWith("set:formation:")) {
    return stay({ ...model, formation: command.slice("set:formation:".length) as Formation, dialog: undefined });
  }
  if (command.startsWith("set:total:")) {
    const size = Number.parseInt(command.slice("set:total:".length), 10);
    if (!Number.isFinite(size)) return stay({ ...model, dialog: undefined });
    // Shrinking the flight can strand a formation the new size may not fly
    // (`solo` is the only legal one for a single aeroplane, and illegal for
    // every other size), so the pair is settled here rather than left for the
    // generator to quietly discard: a choice that no longer applies is dropped.
    const formation = model.formation && formationsFor(size).includes(model.formation) ? model.formation : undefined;
    return stay({ ...model, flightSize: size, formation, dialog: undefined });
  }
  if (command === "close") return stay({ ...model, dialog: undefined });
  if (command === "return") return { model, effect: { kind: "navigate", path: "/" } };
  if (command === "grid") return go({ ...model, slot: undefined, historic: undefined }, "grid");
  if (command === "aerodrome-start-dialog") {
    return stay({ ...model, dialog: aerodromeStartDialog(model.realism.aerodromeStart) });
  }
  if (command === "realism") return go({ ...model, realismReturn: model.view }, "realism");
  if (command === "realism-accept") return go(model, model.realismReturn);
  const view = VIEW_COMMANDS[command];
  if (view) return go(model, view);
  if (command === "begin") {
    const path = missionsLaunchPath(model);
    return path ? { model, effect: { kind: "navigate", path } } : stay(model);
  }
  return stay(model);
}
